import { useMemo } from 'react';

import { type EmployeeGoal } from '../../../shared/api/employee.service';
import { type CountryCode } from '../../../shared/config';
import {
  formatGoalProgressLabelByCountry,
  parseAmount,
} from '../../../shared/model';

type MyPageProfilePreviewProgress = {
  previewProgressLabel: string;
  previewProgressPercent: number;
};

const PREVIEW_PROGRESS_CURRENT_AMOUNT = 1000;
const PREVIEW_PROGRESS_TARGET_AMOUNT = 8000;

function calculatePreviewProgressPercent(
  currentAmount: number,
  targetAmount: number,
) {
  if (
    !Number.isFinite(currentAmount) ||
    !Number.isFinite(targetAmount) ||
    targetAmount <= 0
  ) {
    return 0;
  }

  const completionPercent = Math.round((currentAmount / targetAmount) * 100);

  return Math.max(0, Math.min(100, completionPercent));
}

export function useMyPageProfilePreviewProgress(
  goalAmountValue: string,
  countryCode: CountryCode,
  goal: EmployeeGoal | null = null,
): MyPageProfilePreviewProgress {
  const currentAmount = goal?.currentAmount ?? PREVIEW_PROGRESS_CURRENT_AMOUNT;
  const targetAmount =
    parseAmount(goalAmountValue) ??
    goal?.targetAmount ??
    PREVIEW_PROGRESS_TARGET_AMOUNT;

  return useMemo(() => {
    const previewProgressPercent = calculatePreviewProgressPercent(
      currentAmount,
      targetAmount,
    );

    return {
      previewProgressLabel: formatGoalProgressLabelByCountry(
        currentAmount,
        targetAmount,
        previewProgressPercent,
        countryCode,
      ),
      previewProgressPercent,
    };
  }, [countryCode, currentAmount, targetAmount]);
}
